import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

// 3rd party library
import PropTypes from "prop-types";

// Material Components
import MDBox from "components/MDBox";
import { Card, Divider, IconButton } from "@mui/material";

// Table data
import TowerData from "examples/Drawers/ProjectMap/data/locationData";

// Redux
import { getLocationProgress } from "redux/Thunks/FieldsData";
import {
  getProjectMapQhseData,
  getProjectMapActivityData,
  getProjectMapScopeWiseActivityHoursData,
} from "redux/Thunks/ProjectMap";
import { resetQHSE } from "redux/Slice/ProjectMap";
import { getSnags } from "redux/Thunks/Snag";

// Common Components
import DataTable from "examples/Tables/DataTable";
import ProjectMapDataTable from "examples/Tables/DataTable/ProjectMapLocationCalculation";
import CustomDrawer from "components/Drawer/CustomDrawer";
import pxToRem from "assets/theme/functions/pxToRem";
import MDTypography from "components/MDTypography";
import FontComponent from "components/Responsive/fonts";
import { DrawerBreakPoint } from "components/Responsive/BreakPoints";
import BarChart from "components/Charts/BarChart";
import QhseViewCardDrawer from "examples/Drawers/QHSE/ViewSafetyCard";
import SnagViewModal from "layouts/snag/SnagViewModal";

// Utils
import Constants, { defaultData, Icons, Colors } from "utils/Constants";

import qhseData from "./data/qhseData";
import ProjectMapActivityData from "./data/activityData";
import ProjectMapSnagData from "./data/snagData";

const SNAG_LIST_PER_PAGE = 100;

export default function LocationCalculation({
  viewLocationProgressAnchor,
  setViewLocationProgressAnchor,
  locationData,
  setLocationData,
  showSnags,
}) {
  const [reportProgressData, setReportProgressData] = useState([]);
  const [loading, setLoading] = useState(Constants.PENDING);
  const [activityLoading, setActivityLoading] = useState(Constants.PENDING);
  const [cycleTimeLoading, setCycleTimeLoading] = useState(Constants.PENDING);
  const [activityList, setActivityList] = useState([]);
  const [scopeWiseActivityHoursList, setScopeWiseActivityHoursList] = useState([]);
  const [viewQhse, setViewQhse] = useState(false);
  const [qhseViewId, setQhseViewId] = useState(null);
  const [snagList, setSnagList] = useState([]);
  const [snagLoading, setSnagLoading] = useState(Constants.PENDING);
  const [snagViewId, setSnagViewId] = useState("");
  const dispatch = useDispatch();
  const titleBreakPoint = FontComponent({ sizes: DrawerBreakPoint.baseTitleBreakPoint });

  const resetDrawerState = useCallback(() => {
    setReportProgressData([]);
    setActivityList([]);
    setScopeWiseActivityHoursList([]);
    setViewQhse(false);
    setQhseViewId(null);
    setSnagList([]);
    setSnagLoading(Constants.PENDING);
    setSnagViewId("");
    setLoading(Constants.PENDING);
    setActivityLoading(Constants.PENDING);
    setCycleTimeLoading(Constants.PENDING);
    dispatch(resetQHSE());
  }, [dispatch]);

  const locationProgressData = async () => {
    if (!locationData?.id) {
      setReportProgressData([]);
      setLoading(Constants.FULFILLED);
      return;
    }

    const data2 = {
      id: locationData.id,
    };

    try {
      const res = await dispatch(getLocationProgress(data2));
      if (res?.payload?.status === 200) {
        setReportProgressData(res?.payload?.data?.data ?? []);
      } else {
        setReportProgressData([]);
      }
    } finally {
      setLoading(Constants.FULFILLED);
    }
  };

  const qhseAndActivityListData = async () => {
    if (!locationData?.projectId || !locationData?.id) {
      setActivityList([]);
      setScopeWiseActivityHoursList([]);
      setActivityLoading(Constants.FULFILLED);
      setCycleTimeLoading(Constants.FULFILLED);
      return;
    }

    await dispatch(
      getProjectMapQhseData({ project: locationData.projectId, location: locationData.id })
    );

    try {
      const res = await dispatch(
        getProjectMapActivityData({ project: locationData.projectId, location: locationData.id })
      );
      if (res?.payload?.status === true) {
        setActivityList(res?.payload?.data ?? []);
      } else {
        setActivityList([]);
      }
    } catch (error) {
      setActivityList([]);
    } finally {
      setActivityLoading(Constants.FULFILLED);
    }

    try {
      const scopeWiseList = await dispatch(
        getProjectMapScopeWiseActivityHoursData({
          project: locationData.projectId,
          location: locationData.id,
        })
      );
      if (scopeWiseList?.payload?.status === true) {
        setScopeWiseActivityHoursList(scopeWiseList?.payload?.data ?? []);
      } else {
        setScopeWiseActivityHoursList([]);
      }
    } catch (error) {
      setScopeWiseActivityHoursList([]);
    } finally {
      setCycleTimeLoading(Constants.FULFILLED);
    }
  };

  const snagListData = async () => {
    if (!showSnags || !locationData?.projectId || !locationData?.id) {
      setSnagList([]);
      setSnagLoading(Constants.FULFILLED);
      return;
    }

    try {
      const params = new URLSearchParams({
        page: 0,
        perPage: SNAG_LIST_PER_PAGE,
        sort: "desc",
        project: locationData.projectId,
        location: locationData.id,
      });
      const res = await dispatch(getSnags(params));
      setSnagList(res?.payload?.status === 200 ? res.payload.data?.data?.snagData || [] : []);
    } catch (error) {
      setSnagList([]);
    } finally {
      setSnagLoading(Constants.FULFILLED);
    }
  };

  useEffect(() => {
    if (!locationData?.id || !locationData?.projectId) {
      resetDrawerState();
      return undefined;
    }

    resetDrawerState();
    locationProgressData();
    qhseAndActivityListData();
    snagListData();

    return () => {
      resetDrawerState();
    };
  }, [locationData.id, locationData.projectId, resetDrawerState]);

  const { columns, rows } = TowerData(reportProgressData?.reports);

  const handleCloseLocationProgressDrawer = () => {
    resetDrawerState();
    setViewLocationProgressAnchor({ right: false });
    setLocationData({ id: "", name: "", projectId: "" });
  };

  const handleViewQHSE = useCallback((qhseId) => {
    setViewQhse(true);
    setQhseViewId(qhseId);
  }, []);

  const { qhseColumns, qhseRows } = qhseData({ viewQHSEDetails: handleViewQHSE });

  const { activityColumns, activityRows } = ProjectMapActivityData(activityList);

  const { snagColumns, snagRows } = ProjectMapSnagData(snagList, setSnagViewId);

  return (
    <CustomDrawer
      defaultAnchor={viewLocationProgressAnchor}
      width="90%"
      onDrawerClose={handleCloseLocationProgressDrawer}
    >
      <MDBox
        sx={{
          backgroundColor: Colors.PRIMARY,
          padding: pxToRem(15),
          scrollX: "auto",
          width: "100%",
        }}
        px={pxToRem(24)}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        height={pxToRem(74)}
      >
        <MDBox sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            mr={2}
            aria-label="fingerprint"
            color="info"
            onClick={handleCloseLocationProgressDrawer}
          >
            {Icons.CROSS}
          </IconButton>
          <MDTypography
            sx={{
              color: Colors.WHITE,
              fontFamily: "Inter",
              fontSize: titleBreakPoint,
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: pxToRem(28),
              textAlign: "center",
              textTransform: "none",
            }}
          >
            {`Location Progress (${locationData.name})`}
          </MDTypography>
        </MDBox>
      </MDBox>
      <Divider />
      <MDBox sx={{ padding: pxToRem(30) }} display="flex" gap={3}>
        <Card>
          <MDBox>
            <ProjectMapDataTable
              table={{ columns, rows }}
              isSorted={false}
              entriesPerPage={{ defaultValue: defaultData.PER_PAGE_4 }}
              showTotalEntries={false}
              noEndBorder
              loading={loading}
              towerProgress={
                reportProgressData?.totalCompletion > 0
                  ? `${Math.floor((reportProgressData?.totalCompletion ?? 0) * 100) / 100}%`
                  : ""
              }
            />
          </MDBox>
        </Card>
        <Card>
          <MDTypography
            sx={{
              fontFamily: "Inter",
              fontSize: titleBreakPoint,
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: pxToRem(28),
              textTransform: "none",
              padding: "5px",
            }}
          >
            Cycle Times:
          </MDTypography>
          <BarChart
            title="Hours per Scope"
            data={scopeWiseActivityHoursList}
            valueKey="duration"
            loading={cycleTimeLoading}
          />
          <MDTypography
            sx={{
              fontFamily: "Inter",
              fontSize: titleBreakPoint,
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: pxToRem(28),
              textTransform: "none",
              padding: "5px",
            }}
          >
            QHSE:
          </MDTypography>
          <DataTable
            table={{ columns: qhseColumns, rows: qhseRows }}
            isSorted={false}
            entriesPerPage={{ defaultValue: defaultData.PER_PAGE_2 }}
            showTotalEntries={false}
            noEndBorder
            loading={loading}
            licenseRequired
          />
          {viewQhse && (
            <QhseViewCardDrawer
              viewQhseAnchor={viewQhse}
              qhseViewId={qhseViewId}
              setQhseAnchor={setViewQhse}
              variant="modal"
            />
          )}
          {showSnags && (
            <>
              <MDTypography
                sx={{
                  fontFamily: "Inter",
                  fontSize: titleBreakPoint,
                  fontStyle: "normal",
                  fontWeight: 600,
                  lineHeight: pxToRem(28),
                  textTransform: "none",
                  padding: "5px",
                }}
              >
                Snags:
              </MDTypography>
              <DataTable
                table={{ columns: snagColumns, rows: snagRows }}
                isSorted={false}
                entriesPerPage={{ defaultValue: defaultData.PER_PAGE_2 }}
                showTotalEntries={false}
                noEndBorder
                loading={snagLoading}
                licenseRequired
              />
              {Boolean(snagViewId) && (
                <SnagViewModal
                  open={Boolean(snagViewId)}
                  snagId={snagViewId}
                  handleClose={() => setSnagViewId("")}
                />
              )}
            </>
          )}
          <MDTypography
            sx={{
              fontFamily: "Inter",
              fontSize: titleBreakPoint,
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: pxToRem(28),
              textTransform: "none",
              padding: "5px",
            }}
          >
            Latest Activities:
          </MDTypography>
          <DataTable
            table={{ columns: activityColumns, rows: activityRows }}
            isSorted={false}
            entriesPerPage={{ defaultValue: defaultData.PER_PAGE }}
            showTotalEntries={false}
            noEndBorder
            loading={activityLoading}
            licenseRequired
          />
        </Card>
      </MDBox>
    </CustomDrawer>
  );
}

LocationCalculation.propTypes = {
  viewLocationProgressAnchor: PropTypes.shape({
    right: PropTypes.bool,
  }),
  setViewLocationProgressAnchor: PropTypes.func.isRequired,
  locationData: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    projectId: PropTypes.string,
  }),
  setLocationData: PropTypes.func.isRequired,
  showSnags: PropTypes.bool,
};

LocationCalculation.defaultProps = {
  viewLocationProgressAnchor: { right: false },
  locationData: {
    id: "",
    name: "",
  },
  showSnags: false,
};
